import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { X, Flame, Trophy, Heart, Utensils } from "lucide-react";

export default function RelationshipCard({ open, conversationId, conversationName, onClose }) {
  const [data, setData] = useState(null);
  const [feeding, setFeeding] = useState(false);
  const [feedNote, setFeedNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/conversations/${conversationId}/relationship`);
      setData(data);
    } catch {}
  };

  useEffect(() => {
    if (!open || !conversationId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversationId]);

  const feed = async () => {
    setFeeding(true);
    setFeedNote("");
    try {
      const { data: pet } = await api.post(`/conversations/${conversationId}/pet/feed`);
      setData((d) => (d ? { ...d, pet } : d));
      setFeedNote(pet.love_gained ? `+${pet.love_gained} sevgi ❤️` : "");
      setTimeout(() => setFeedNote(""), 2500);
    } catch (e) {
      setFeedNote(formatApiError(e));
      setTimeout(() => setFeedNote(""), 3500);
    }
    setFeeding(false);
  };

  if (!open) return null;

  const lvl = data?.level;
  const pct = lvl ? Math.min(100, Math.round((lvl.xp_into_level / lvl.xp_needed) * 100)) : 0;
  const badgesEarned = (data?.badges || []).filter((b) => b.earned);
  const badgesLocked = (data?.badges || []).filter((b) => !b.earned);
  const pet = data?.pet || {};

  // Pet next-stage progress
  const stageStart = pet.love_needed || 0;
  const stageEnd = pet.next_love_needed;
  const stagePct = stageEnd
    ? Math.min(100, Math.round(((pet.love - stageStart) / (stageEnd - stageStart)) * 100))
    : 100;
  const hungerPct = Math.max(0, Math.min(100, pet.hunger || 0));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="relationship-card">
      <div className="bg-white border-hard shadow-hard-lg rounded-2xl w-full max-w-lg p-6 slide-up max-h-[85vh] overflow-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl flex items-center gap-2"><Trophy strokeWidth={3} className="w-6 h-6" /> Arkadaşlık</h2>
          <button onClick={onClose} data-testid="relationship-close-btn" className="w-9 h-9 border-hard-2 rounded-lg flex items-center justify-center press-btn">
            <X strokeWidth={3} className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-[#555] mb-4"><b>{conversationName}</b> ile ilişkiniz</p>

        {!data && <div className="text-center py-8 font-semibold">Yükleniyor…</div>}

        {data && (
          <>
            {/* Level bar */}
            <div className="border-hard rounded-2xl p-4 bg-gradient-to-br from-[#D4B3FF] to-[#F0DFFF] shadow-hard-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase font-black tracking-widest">Seviye</div>
                  <div className="font-display text-4xl">{lvl?.level}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 bg-white border-hard-2 rounded-full px-2 py-0.5" data-testid="streak-chip">
                    <Flame strokeWidth={3} className="w-4 h-4 text-[#FF7A00]" />
                    <span className="text-sm font-black">{data.streak}g</span>
                  </div>
                  <div className="flex items-center gap-1 bg-white border-hard-2 rounded-full px-2 py-0.5">
                    <Heart strokeWidth={3} className="w-4 h-4 text-[#E43385]" />
                    <span className="text-sm font-black">{data.msg_count}</span>
                  </div>
                </div>
              </div>
              <div className="mt-3 h-3 bg-white border-hard-2 rounded-full overflow-hidden">
                <motion.div className="h-full bg-[#0A0A0A]" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} />
              </div>
              <div className="mt-1 text-[11px] font-bold text-[#0A0A0A]">{lvl?.xp_into_level}/{lvl?.xp_needed} XP</div>
            </div>

            {/* Pet card */}
            <div className="mt-4 border-hard-2 rounded-2xl p-4">
              <div className="flex items-center gap-4">
                <motion.div key={pet.emoji} initial={{ scale: 0.7 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="text-5xl">
                  {pet.emoji || "🥚"}
                </motion.div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg">{pet.name || "Yumurta"}</div>
                  {pet.next_emoji ? (
                    <div className="text-xs text-[#555]">
                      Sonraki: <span className="text-lg align-middle">{pet.next_emoji}</span> {pet.next_name} — <b>{pet.next_love_needed - (pet.love || 0)} sevgi</b> kaldı
                    </div>
                  ) : (
                    <div className="text-xs text-[#5C9A00] font-black">Efsanevi seviyeye ulaştın! 🎊</div>
                  )}
                </div>
              </div>

              {/* Stage progress */}
              {pet.next_love_needed && (
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] uppercase font-black tracking-widest">
                    <span>Evrim ilerlemesi</span>
                    <span>{pet.love}/{pet.next_love_needed}</span>
                  </div>
                  <div className="mt-1 h-2.5 bg-[#F9F8F6] border-hard-2 rounded-full overflow-hidden">
                    <motion.div className="h-full bg-[#5C9A00]" initial={{ width: 0 }} animate={{ width: `${stagePct}%` }} transition={{ duration: 0.6 }} />
                  </div>
                </div>
              )}

              {/* Hunger */}
              <div className="mt-3">
                <div className="flex justify-between text-[10px] uppercase font-black tracking-widest">
                  <span className="flex items-center gap-1"><Utensils strokeWidth={3} className="w-3 h-3" /> Açlık</span>
                  <span data-testid="pet-hunger-pct">{hungerPct}%</span>
                </div>
                <div className="mt-1 h-2.5 bg-[#F9F8F6] border-hard-2 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full"
                    style={{ background: hungerPct > 60 ? "#FF3B30" : hungerPct > 30 ? "#FF7A00" : "#CCFF00" }}
                    initial={{ width: 0 }}
                    animate={{ width: `${hungerPct}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>

              {/* Feed button + info */}
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={feed}
                  disabled={feeding || !pet.can_feed}
                  data-testid="pet-feed-btn"
                  className="bg-[#CCFF00] border-hard rounded-xl px-4 py-2 font-black text-sm press-btn shadow-hard-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <Utensils strokeWidth={3} className="w-4 h-4" /> Besle ({pet.feeds_today || 0}/{pet.max_feeds_per_day || 3})
                </button>
                <div className="text-xs text-[#555] flex-1" data-testid="feed-note">
                  {feedNote ? <b className="text-[#0A0A0A]">{feedNote}</b> : (
                    pet.can_feed
                      ? `Açlık %${MIN_HUNGER_TO_FEED_DEF}+ olunca daha çok sevgi kazandırır 🌟`
                      : (pet.feeds_today >= (pet.max_feeds_per_day || 3)
                          ? "Bugünlük besleme hakkın bitti. Yarın tekrar."
                          : `Şu an aç değil (açlık %${MIN_HUNGER_TO_FEED_DEF}+ olmalı). Zamanla acıkacak.`)
                  )}
                </div>
              </div>
              <p className="mt-3 text-[11px] text-[#555]">
                💡 <b>Nasıl büyür?</b> Sadece <b>karşılıklı</b> yazışınca sevgi kazanır (aynı kişiden art arda mesaj sayılmaz). Karşı taraftan cevap gelirse +1, 5 dakika içinde hızlı cevap ise +2 sevgi. Ayrıca açlığı %25+ olduğunda günde en fazla 3 kez besleyebilirsin — ne kadar açsa o kadar sevgi kazandırır.
              </p>
            </div>

            {/* Badges */}
            <div className="mt-4">
              <div className="text-xs uppercase font-black tracking-widest mb-2">Rozetler</div>
              <div className="flex items-center gap-2 flex-wrap">
                {badgesEarned.map((b) => (
                  <div key={b.key} data-testid={`badge-earned-${b.key}`} className="flex items-center gap-1 border-hard-2 rounded-full px-3 py-1 bg-[#CCFF00] shadow-hard-sm">
                    <span className="text-lg">{b.emoji}</span>
                    <span className="text-xs font-black">{b.label}</span>
                  </div>
                ))}
                {badgesLocked.map((b) => (
                  <div key={b.key} title={b.condition} data-testid={`badge-locked-${b.key}`} className="flex items-center gap-1 border-hard-2 rounded-full px-3 py-1 bg-white opacity-50">
                    <span className="text-lg grayscale">{b.emoji}</span>
                    <span className="text-xs font-black">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const MIN_HUNGER_TO_FEED_DEF = 25;

